import React from 'react';
import { motion } from 'framer-motion';

const SatisfiedClientsSection = () => {
  const benefits = [
    {
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
        </svg>
      ),
      title: 'Rigueur de Classe Mondiale',
      description: 'Une méthodologie d’élite adaptée aux réalités des marchés émergents.'
    },
    {
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"/>
        </svg>
      ),
      title: 'Écosystème de Décideurs',
      description: 'Intégrez un réseau influent de leaders et de bâtisseurs du continent.'
    },
    {
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z"/>
        </svg>
      ),
      title: 'Impact Haute Vélocité',
      description: 'Accélérez votre croissance avec des résultats visibles dès le premier trimestre.'
    },
    {
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"/>
        </svg>
      ),
      title: 'Mentorat Stratégique',
      description: 'Un accompagnement de proximité par des experts seniors dédiés.'
    }
  ];

  const testimonials = [
    {
      quote: "L\'approche du cabinet a redéfini notre vision stratégique. Leur maîtrise des enjeux locaux est un atout rare.",
      author: "M. Ibrahima Sow",
      position: "Directeur de l\'Innovation",
      company: "Atlantique Télécom",
      country: "Bénin"
    },
    {
      quote: "La qualité de l\'ingénierie pédagogique a permis une montée en compétence fulgurante de nos cadres supérieurs.",
      author: "Dra. Fatoumata Bamba",
      position: "VP Capital Humain",
      company: "Sifca Groupe",
      country: "Côte d\'Ivoire"
    },
    {
      quote: "Un partenaire indispensable qui allie intégrité, agilité et excellence opérationnelle.",
      author: "Eng. Samuel Adeyemi",
      position: "Chief Operations Officer",
      company: "Lagos Power Solutions",
      country: "Nigéria"
    }
  ];

  return (
    <section className="relative py-20 bg-white overflow-hidden">
      {/* Background soft shapes */}
      <div className="absolute top-0 left-0 w-full h-full pointer-events-none">
        <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-[#9B8E57]/5 rounded-full blur-[80px]" />
        <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-[#96121D]/5 rounded-full blur-[80px]" />
      </div>
      <div className="relative z-10 max-w-7xl mx-auto px-6">
        {/* Header Section */}
        <div className="text-center mb-20">
          <motion.h2 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            className="text-3xl md:text-5xl font-black text-gray-900 tracking-tight"
          >
            Ils bâtissent l'avenir avec <span className="text-[#96121D]">CRF Perfection</span>
          </motion.h2>
          <p className="mt-6 text-lg text-gray-500 max-w-2xl mx-auto">
            Plus qu'un cabinet, un moteur de performance pour les leaders africains en quête d'excellence.
          </p>
          <div className="mt-8 flex justify-center gap-2">
            <div className="w-12 h-1 rounded-full bg-[#96121D]" />
            <div className="w-4 h-1 rounded-full bg-[#9B8E57]" />
          </div>
        </div>

        {/* Value Props Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-24">
          {benefits?.map((benefit, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ delay: index * 0.1 }}
              className="p-8 rounded-3xl bg-gray-50 border border-transparent hover:border-[#9B8E57]/30 hover:bg-white hover:shadow-2xl hover:shadow-[#9B8E57]/5 transition-all duration-300"
            >
              <div className="w-12 h-12 flex items-center justify-center rounded-2xl bg-white shadow-sm text-[#96121D] mb-6">
                {benefit?.icon}
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-3">{benefit?.title}</h3>
              <p className="text-sm text-gray-500 leading-relaxed font-medium">{benefit?.description}</p>
            </motion.div>
          ))}
        </div>

        {/* Quotes Section */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-20">
          {testimonials?.map((t, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.2 }}
              className="relative p-10 bg-white rounded-[2rem] border border-gray-100 shadow-sm"
            >
              <span className="absolute top-6 right-10 text-6xl font-serif text-[#9B8E57]/10">“</span>
              <p className="text-gray-600 font-medium italic mb-10 leading-relaxed relative z-10">
                « {t?.quote} »
              </p>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-gradient-to-tr from-[#96121D] to-[#9B8E57] flex items-center justify-center text-white font-bold">
                  {t?.author?.charAt(3)}
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 text-sm">{t?.author}</h4>
                  <p className="text-[11px] font-bold text-[#96121D] uppercase tracking-widest">{t?.position}</p>
                  <p className="text-[10px] text-gray-400">{t?.company} • {t?.country}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Final CTA Card */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          className="relative rounded-[3rem] bg-[#1a1a1a] p-10 md:p-16 overflow-hidden text-center"
        >
          {/* Decorative light effect */}
          <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-[#96121D]/20 to-transparent pointer-events-none" />
          
          <div className="relative z-10">
            <h3 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Votre transformation commence ici
            </h3>
            <p className="text-gray-400 max-w-xl mx-auto mb-10 text-lg">
              Rejoignez les institutions qui font bouger les lignes. Planifiez votre première session de conseil aujourd'hui.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <a
                href="/information-request-form"
                className="px-10 py-5 bg-[#96121D] text-white font-bold rounded-2xl hover:bg-[#b31926] hover:scale-105 transition-all shadow-xl shadow-red-950/20"
              >
                Inscrire mon organisation
              </a>
              <a
                href="/contact"
                className="px-10 py-5 bg-transparent border-2 border-white/20 text-white font-bold rounded-2xl hover:bg-white hover:text-black hover:border-white transition-all"
              >
                Parler à un expert
              </a>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default SatisfiedClientsSection;